import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  isLowPowerDevice,
  requestIdleCallback,
} from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const NO_SWIPE_SELECTOR =
  'model-viewer, video, iframe, .deferred-media__poster-button, product-model';

/**
 * Full-screen zoom dialog with Swiper (main + bottom thumbs).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} mainSwiperEl
 * @property {HTMLElement} slidesContainer
 * @property {HTMLElement[]} media
 * @property {HTMLElement} [thumbsWrap]
 * @property {HTMLElement} [thumbsSwiperEl]
 * @property {HTMLElement} [thumbsSlidesContainer]
 *
 * @extends {Component<Refs>}
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'mainSwiperEl', 'slidesContainer'];

  /** @type {import('swiper').Swiper | null} */
  #mainSwiper = null;

  /** @type {import('swiper').Swiper | null} */
  #thumbsSwiper = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    requestIdleCallback(() => {
      this.#waitForSwiper();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.#destroySwipers();
  }

  #handleCancel = (event) => {
    event.preventDefault();
  };

  async #waitForSwiper() {
    if (window.Swiper) return;

    await new Promise((resolve) => {
      const check = () => {
        if (window.Swiper) {
          resolve();
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  #destroySwipers() {
    if (this.#mainSwiper?.params?.thumbs) {
      this.#mainSwiper.params.thumbs.swiper = null;
    }

    this.#mainSwiper?.destroy(true, true);
    this.#thumbsSwiper?.destroy(true, true);
    this.#mainSwiper = null;
    this.#thumbsSwiper = null;
  }

  get #mediaSlides() {
    const container = this.refs.slidesContainer;
    if (!(container instanceof HTMLElement)) return [];

    return Array.from(container.querySelectorAll('.swiper-slide[ref="media[]"], .swiper-slide[ref]'));
  }

  #syncThumbsVisibility() {
    const thumbsWrap = this.refs.thumbsWrap;
    if (!(thumbsWrap instanceof HTMLElement)) return;

    thumbsWrap.hidden = this.#mediaSlides.length <= 1;
  }

  async #ensureSwipers() {
    await this.#waitForSwiper();
    if (!this.isConnected || !this.refs.dialog.open) return;

    if (!this.#mainSwiper) {
      this.#initSwipers();
    }

    this.#updateAllSwipers();
    this.#syncThumbsVisibility();
  }

  #initSwipers() {
    const Swiper = window.Swiper;
    if (!Swiper || this.#mainSwiper || !this.refs.dialog.open) return;

    const thumbsEl = this.refs.thumbsSwiperEl;
    const thumbConfig = {
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: true,
      watchSlidesProgress: true,
      direction: 'horizontal',
      centeredSlides: true,
      centeredSlidesBounds: true,
    };

    if (thumbsEl instanceof HTMLElement) {
      this.#thumbsSwiper = new Swiper(thumbsEl, thumbConfig);
    }

    const linkedThumbs = this.#thumbsSwiper;
    const speed = prefersReducedMotion() ? 0 : 480;

    /** @type {import('swiper').SwiperOptions} */
    const mainConfig = {
      effect: 'slide',
      speed,
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 0,
      grabCursor: true,
      resistanceRatio: 0.85,
      longSwipesRatio: 0.25,
      threshold: 8,
      watchOverflow: true,
      noSwipingSelector: NO_SWIPE_SELECTOR,
      on: {
        slideChange: () => this.#onSlideChange(),
      },
    };

    if (linkedThumbs) {
      mainConfig.thumbs = {
        swiper: linkedThumbs,
        multipleActiveThumbs: false,
      };
    }

    this.#mainSwiper = new Swiper(this.refs.mainSwiperEl, mainConfig);
    this.#syncThumbsVisibility();
  }

  #updateAllSwipers() {
    this.#thumbsSwiper?.update();
    this.#mainSwiper?.update();
    this.#syncThumbsVisibility();
  }

  #onSlideChange() {
    const index = this.#mainSwiper?.activeIndex ?? 0;
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * @param {number} index
   * @param {{ animate?: boolean }} [options]
   */
  async #afterOpen(index, options = { animate: false }) {
    await this.#ensureSwipers();
    this.#mainSwiper?.update();
    this.#thumbsSwiper?.update();
    this.#goToSlide(index, options);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog } = this.refs;
    const media = this.#mediaSlides;
    const targetImage = media[index];

    /** @type {HTMLElement | null} */
    const sourceImage =
      (event.target instanceof Element
        ? event.target.closest('li,slideshow-slide,.swiper-slide')
        : null) ??
      this.closest('media-gallery')?.swiper?.getSlideElement(index) ??
      null;

    const useViewTransition =
      supportsViewTransitions() && !isLowPowerDevice() && !prefersReducedMotion() && sourceImage && targetImage;

    if (useViewTransition) {
      const itemTransitionName = `gallery-item-open`;
      sourceImage.style.setProperty('view-transition-name', itemTransitionName);

      const focalPoint = sourceImage.dataset.focalPoint;
      if (focalPoint) {
        document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
      }

      await startViewTransition(() => {
        dialog.showModal();
        sourceImage.style.removeProperty('view-transition-name');
        targetImage.style.setProperty('view-transition-name', itemTransitionName);
      });

      targetImage.style.removeProperty('view-transition-name');
      document.documentElement.style.removeProperty('--gallery-media-focal-point');
    } else {
      dialog.showModal();
    }

    await this.#afterOpen(index, { animate: false });
  }

  /**
   * @param {number} index
   * @param {{ animate?: boolean }} [options]
   */
  #goToSlide(index, options = {}) {
    const { animate = !prefersReducedMotion() } = options;
    const speed = animate ? 400 : 0;

    if (this.#mainSwiper) {
      this.#mainSwiper.slideTo(index, speed);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog } = this.refs;
    const media = this.#mediaSlides;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const activeIndex = this.#mainSwiper?.activeIndex ?? 0;
    const mostVisibleElement = media[activeIndex] ?? media[0];
    if (!mostVisibleElement) return this.closeDialog();

    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    let slide = null;
    if (slideshowActive && mediaGallery?.swiper) {
      slide = mediaGallery.swiper.getSlideElement(activeIndex);
    } else if (slideshowActive && mediaGallery?.slideshow) {
      slide = mediaGallery.slideshow.slides?.[activeIndex];
    } else {
      slide = mediaGallery?.media?.[activeIndex];
    }

    if (!slide) return this.closeDialog();

    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');
    await onAnimationEnd(this.refs.dialog);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
    }
  }

  /**
   * @param {number} index
   */
  async handleThumbnailClick(index) {
    await this.#ensureSwipers();
    this.#goToSlide(index);
  }

  /**
   * @param {number} index
   * @param {{ behavior?: ScrollBehavior }} [options]
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (isNaN(index) || index < 0 || index >= this.#mediaSlides.length) return;

    await this.#ensureSwipers();
    const animate = options.behavior !== 'instant' && !prefersReducedMotion();
    this.#goToSlide(index, { animate });
  }

  updatedCallback() {
    super.updatedCallback();
  }

  /**
   * Refresh Swiper after DOM changes (e.g. AI-generated media).
   */
  async refreshSwipers() {
    if (typeof this.updatedCallback === 'function') {
      this.updatedCallback();
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));
    await this.#ensureSwipers();
  }

  /**
   * @param {HTMLElement} slide
   */
  appendMediaSlide(slide) {
    const container = this.refs.slidesContainer;
    if (container instanceof HTMLElement) {
      container.appendChild(slide);
    }
  }

  /**
   * @param {HTMLElement} thumbSlide
   */
  appendThumbSlide(thumbSlide) {
    const container = this.refs.thumbsSlidesContainer;
    if (container instanceof HTMLElement) {
      container.appendChild(thumbSlide);
    }
  }

  /**
   * @param {string} selector
   */
  removeThumbSlides(selector) {
    this.querySelectorAll(selector).forEach((node) => node.remove());
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
